import type { RouteTypes } from '@energovizor/api-client';
import dayjs from 'dayjs';
import utc from 'dayjs/plugin/utc';
import timezone from 'dayjs/plugin/timezone';

dayjs.extend(utc);
dayjs.extend(timezone);
export const SOLAR_TIMEZONE = 'Europe/Prague';
export type SolarHistory = NonNullable<RouteTypes['/solar-connections/history']['get']['response']['payload']>;
export type SolarHistorySeries = SolarHistory['series'][number] & { sourceIds?: string[] };
export type SolarHistoryPoint = SolarHistorySeries['points'][number];
export type SolarGrain = 'quarterHour' | 'hour' | 'day' | 'month' | 'year';
export type SolarScope = { collectionPointIds?: string[]; buildingId?: string; groupId?: string; municipalityId?: string };
export type SolarRow = { period: string; sortKey: number; [key: string]: string | number | null };
const colors = ['#d18b00', '#19877e', '#bd5275', '#6574cd', '#cf6232', '#498abb', '#94704d'];
export const solarDate = (date: string | number | Date) => typeof date === 'string' && /^\d{4}-\d{2}-\d{2}$/.test(date) ? dayjs.tz(date, SOLAR_TIMEZONE) : dayjs(date).tz(SOLAR_TIMEZONE);
export function isValidPragueCalendarDate(value: string): boolean {
  if (!/^\d{4}-\d{2}-\d{2}$/.test(value)) return false;
  try {
    const date = solarDate(value);
    return date.isValid() && date.format('YYYY-MM-DD') === value;
  } catch {
    return false;
  }
}
export const solarColor = (id: string) => colors[Math.abs([...id].reduce((sum, char) => (sum * 31 + char.charCodeAt(0)) | 0, 0)) % colors.length]!;
export const solarSeriesLabel = (series: SolarHistorySeries) => `${series.metricLabel} · ${series.provider === 'aggregate' ? 'Součet fotovoltaiky' : series.provider === 'dzd' ? 'DZD' : 'SolaX'} · ${series.buildingName} / ${series.collectionPointName} · ${series.label} (${series.deviceId})${series.retired ? ' · historie' : ''}`;
export const solarPeriodKey = (date: string | number | Date, grain: SolarGrain) => solarDate(date).format(grain === 'year' ? 'YYYY' : grain === 'month' ? 'YYYY-MM' : grain === 'day' ? 'YYYY-MM-DD' : 'YYYY-MM-DDTHH:mmZ');
const clockLabelFormatter = new Intl.DateTimeFormat('en-GB', { timeZone: SOLAR_TIMEZONE, day: '2-digit', month: '2-digit', year: 'numeric', hour: '2-digit', minute: '2-digit', hourCycle: 'h23', timeZoneName: 'shortOffset' });
function clockLabelParts(instant: number) {
  return Object.fromEntries(clockLabelFormatter.formatToParts(instant).map(part => [part.type, part.value]));
}
export function solarPeriodLabel(date: string | number | Date, grain: SolarGrain) {
  const value = solarDate(date);
  if (grain === 'year') return value.format('YYYY');
  if (grain === 'month') return value.format('MM/YYYY');
  if (grain === 'day') return value.format('DD.MM.YYYY');
  // Only repeated autumn clock hours need an offset to distinguish their labels.
  const parts = clockLabelParts(value.valueOf());
  const wallTime = (item: Record<string, string>) => `${item.year}-${item.month}-${item.day} ${item.hour}:${item.minute}`;
  const ambiguous = [-3600000, 3600000].some(offset => wallTime(clockLabelParts(value.valueOf() + offset)) === wallTime(parts));
  const offset = parts.timeZoneName!.replace('GMT', '').replace(/^([+-])(\d)$/, '$10$2:00');
  return `${parts.day}.${parts.month}. ${parts.hour}:${parts.minute}${ambiguous ? ` ${offset}` : ''}`;
}
export function defaultSolarWindow() {
  const to = solarDate(new Date());
  return { from: to.subtract(29, 'day').format('YYYY-MM-DD'), to: to.format('YYYY-MM-DD') };
}
export function solarWindowForRows(rows: { sortKey: number }[], grain: SolarGrain) {
  if (rows.length === 0) return defaultSolarWindow();
  const dates = rows.map(row => grain === 'year' && row.sortKey < 10000 ? `${row.sortKey}-01-01` : row.sortKey);
  const times = dates.map(date => solarDate(date).valueOf());
  const unit = grain === 'year' ? 'year' : grain === 'month' ? 'month' : 'day';
  return { from: solarDate(Math.min(...times)).startOf(unit).format('YYYY-MM-DD'), to: solarDate(Math.max(...times)).endOf(unit).format('YYYY-MM-DD') };
}

export function solarChartSeries(series: SolarHistorySeries[], yAxisId?: string) {
  return series.map(item => ({
    id: `solar:${item.id}`, dataKey: `solar:${item.id}`, label: solarSeriesLabel(item), color: solarColor(item.id),
    ...(yAxisId ? { yAxisId } : {}),
    valueFormatter: (value: number | null) => value === null ? 'Bez dat' : `${value.toLocaleString('cs-CZ', { maximumFractionDigits: 2 })} ${item.unit}`,
  }));
}

// Use the merged dataset indexes, because different providers may have different
// missing buckets. Isolated readings must stay visible without bridging gaps.
export function solarLineSeries(series: SolarHistorySeries[], rows: ReadonlyArray<Record<string, unknown>>, yAxisId?: string) {
  return solarChartSeries(series, yAxisId).map(item => {
    const values = rows.map(row => typeof row[item.dataKey] === 'number' ? row[item.dataKey] : null);
    const sparse = values.filter(value => value !== null).length <= 30;
    return { ...item, curve: 'linear' as const, connectNulls: false,
      showMark: ({ index }: { index: number }) => values[index] !== null && values[index] !== undefined && (sparse || (values[index - 1] == null && values[index + 1] == null)),
    };
  });
}

// Match calendar periods explicitly rather than trusting the browser's local timezone.
export function mergeSolarRows(rows: SolarRow[], series: SolarHistorySeries[], grain: SolarGrain): SolarRow[] {
  const byPeriod = new Map<string, SolarRow>();
  for (const row of rows) {
    const date = grain === 'year' && row.sortKey < 10000 ? `${row.sortKey}-01-01` : row.sortKey;
    const key = solarPeriodKey(date, grain);
    const existing = byPeriod.get(key);
    if (!existing) byPeriod.set(key, { ...row, sortKey: grain === 'year' ? Number(key) : row.sortKey });
    else for (const [field, value] of Object.entries(row)) {
      if (field === 'period' || field === 'sortKey') continue;
      existing[field] = typeof value === 'number' && typeof existing[field] === 'number' ? existing[field] + value : value;
    }
  }
  const periodKeys = new Map<string, string>();
  for (const item of series) for (const point of item.points) {
    let key = periodKeys.get(point.start);
    if (!key) { key = solarPeriodKey(point.start, grain); periodKeys.set(point.start, key); }
    const row = byPeriod.get(key) ?? { period: solarPeriodLabel(point.start, grain), sortKey: grain === 'year' ? Number(key) : Date.parse(point.start) };
    row[`solar:${item.id}`] = point.value;
    byPeriod.set(key, row);
  }
  return [...byPeriod.values()].sort((a, b) => a.sortKey - b.sortKey).map(row => {
    for (const item of series) if (!(`solar:${item.id}` in row)) row[`solar:${item.id}`] = null;
    return row;
  });
}

export function solarProductionSummary(series: SolarHistorySeries[]) {
  const coveredSourceIds = new Set(series.flatMap(item => item.sourceIds ?? []));
  const primary = series.filter(item => item.metric === 'production' && !item.duplicateOf && !coveredSourceIds.has(item.id));
  const points = primary.flatMap(item => item.points);
  const values = points.filter(point => point.value !== null);
  return { value: values.length ? values.reduce((sum, point) => sum + point.value!, 0) : null, incomplete: points.some(point => point.incomplete || point.value === null) };
}
export const EMPTY_SOLAR_SERIES: SolarHistorySeries[] = [];

export function visibleSolarSeries(series: SolarHistorySeries[], hidden: Set<string>) { return series.filter(item => !hidden.has(item.id)); }
export function toggleSolarSeries(hidden: Set<string>, id: string) {
  const next = new Set(hidden);
  if (next.has(id)) next.delete(id); else next.add(id);
  return next;
}
export function toggleAllSolarSeries(hidden: Set<string>, series: SolarHistorySeries[]) {
  const next = new Set(hidden);
  const hide = series.some(item => !hidden.has(item.id));
  series.forEach(item => { if (hide) next.add(item.id); else next.delete(item.id); });
  return next;
}

export function aggregateSolarProduction(series: SolarHistorySeries[], grouping: 'collectionPoint' | 'building' | 'total'): SolarHistorySeries[] {
  const groups = new Map<string, SolarHistorySeries[]>();
  for (const item of series) {
    if (item.metric !== 'production' || item.duplicateOf || item.provider === 'aggregate') continue;
    const key = grouping === 'total' ? 'total' : grouping === 'building' ? item.buildingId : item.collectionPointId;
    const group = groups.get(key) ?? [];
    group.push(item);
    groups.set(key, group);
  }
  const aggregates: SolarHistorySeries[] = [];
  for (const [key, sources] of groups) {
    if (sources.length < 2) continue;
    const byStart = new Map<string, SolarHistoryPoint[]>();
    for (const source of sources) for (const point of source.points) {
      const points = byStart.get(point.start) ?? [];
      points.push(point);
      byStart.set(point.start, points);
    }
    const first = sources[0]!;
    aggregates.push({ ...first, id: `production-total:${grouping}:${key}`, provider: 'aggregate', connectionId: '', duplicateOf: null,
      label: grouping === 'total' ? 'Celkem vybrané objekty' : grouping === 'building' ? first.buildingName : first.collectionPointName,
      deviceId: `${sources.length} zdrojů`, sourceIds: sources.map(item => item.id), inverterSerial: '', retired: sources.every(item => item.retired),
      points: [...byStart.values()].map(points => {
        const values = points.filter(point => point.value !== null);
        return { ...points[0]!, value: values.length ? values.reduce((sum, point) => sum + point.value!, 0) : null,
          min: null, max: null, coverage: points.reduce((sum, point) => sum + point.coverage, 0) / sources.length,
          incomplete: points.length < sources.length || points.some(point => point.incomplete || point.value === null),
          estimated: points.some(point => point.estimated), ongoing: points.some(point => point.ongoing) };
      }).sort((a, b) => a.start.localeCompare(b.start)),
    });
  }
  return [...series, ...aggregates];
}
